import React from 'react';
import { genId } from './genId';
import { PagesManager, isPageVisible } from '@/components/dashboards/PageNavChrome';

const newPage = (count) => ({ id: genId('page_'), name: `Page ${count + 1}`, visible: true });
const initialVisual = () => ({
  id: genId('v'), vizType: 'bar_chart', title: 'Visual 1',
  rowDims: [], colDim: '', valueMeasures: [], filterDims: [],
});

// Author-only page management for dashboard mode: add / rename / reorder /
// duplicate / delete / hide + per-page icon & description + the dashboard's
// navigation style. Backed by the shared PagesManager UI.
export function DashboardPagesPanel({
  pages, setPages, activePageId, setActivePageId,
  pageVisuals, setPageVisuals, gridLayouts, setGridLayouts,
  pageNavStyle, setPageNavStyle,
}) {
  const patchPage = (id, patch) => setPages(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));

  const addPage = () => {
    setPages(prev => {
      const p = newPage(prev.length);
      setPageVisuals(pv => ({ ...pv, [p.id]: [initialVisual()] }));
      setActivePageId(p.id);
      return [...prev, p];
    });
  };

  const duplicatePage = (id) => {
    const src = pages.find(p => p.id === id);
    if (!src) return;
    const newId = genId('page_');
    const srcVisuals = pageVisuals[id] || [];
    // Clone visuals with fresh ids; carry layout entries across.
    const idMap = {};
    const copies = srcVisuals.map(v => { const nid = genId('v'); idMap[v.id] = nid; return { ...v, id: nid }; });
    setPageVisuals(pv => ({ ...pv, [newId]: copies }));
    if (gridLayouts?.lg && setGridLayouts) {
      const extra = gridLayouts.lg
        .filter(l => idMap[l.i])
        .map(l => ({ ...l, i: idMap[l.i] }));
      if (extra.length) setGridLayouts(prev => ({ ...(prev || {}), lg: [...(prev?.lg || []), ...extra] }));
    }
    setPages(prev => {
      const idx = prev.findIndex(p => p.id === id);
      const next = [...prev];
      next.splice(idx + 1, 0, { id: newId, name: `${src.name} (Copy)`, visible: isPageVisible(src), icon: src.icon, description: src.description });
      return next;
    });
    setActivePageId(newId);
  };

  const deletePage = (id) => {
    if (pages.length <= 1) return;
    setPageVisuals(pv => { const next = { ...pv }; delete next[id]; return next; });
    setPages(prev => {
      const next = prev.filter(p => p.id !== id);
      if (activePageId === id) setActivePageId(next[0]?.id);
      return next;
    });
  };

  const reorder = (from, to) => {
    setPages(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  // Reorder whole sidebar sections (named groups + the implicit ungrouped block).
  // Rebuilds the pages array so the moved section's pages travel together,
  // preserving each page's order within its section.
  const reorderGroups = (from, to) => {
    setPages(prev => {
      const order = [];
      prev.forEach(p => { const g = p.group || ''; if (!order.includes(g)) order.push(g); });
      if (from < 0 || to < 0 || from >= order.length || to >= order.length) return prev;
      const [moved] = order.splice(from, 1);
      order.splice(to, 0, moved);
      const next = [];
      order.forEach(g => prev.forEach(p => { if ((p.group || '') === g) next.push(p); }));
      return next;
    });
  };

  return (
    <div className="p-3 border-b border-border/30">
      <PagesManager
        pages={pages}
        activeId={activePageId}
        navStyle={pageNavStyle}
        onSelect={setActivePageId}
        onNavStyle={setPageNavStyle}
        onAdd={addPage}
        onRename={(id, name) => patchPage(id, { name })}
        onDuplicate={duplicatePage}
        onDelete={deletePage}
        onToggleHidden={(id) => setPages(prev => prev.map(p => p.id === id ? { ...p, visible: isPageVisible(p) ? false : true } : p))}
        onReorder={reorder}
        onSetIcon={(id, icon) => patchPage(id, { icon })}
        onSetDescription={(id, description) => patchPage(id, { description })}
        onSetGroup={(id, group) => patchPage(id, { group })}
        onReorderGroups={reorderGroups}
      />
    </div>
  );
}
